// RC8 · Ship Reference shared helpers. Pure functions — no network, no React.

// Mirror the backend `_slugify` so the reference endpoints can resolve by a
// durable name-slug when the voyage carries no explicit ship_id.
export function slugify(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// Widgety descriptions arrive as HTML fragments. Render them as plain text
// (no dangerouslySetInnerHTML → no XSS surface): strip tags, turn block/break
// tags into spaces, decode the few common entities.
export function toPlainText(html) {
  if (!html) return "";
  return String(html)
    .replace(/<\s*(br|\/p|\/li|\/div)\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&#39;|&rsquo;|&apos;/gi, "\u2019")
    .replace(/&quot;/gi, '"')
    .replace(/\s+/g, " ")
    .trim();
}

export function formatUpdated(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return null;
  }
}

export function formatReservationDate(iso) {
  if (!iso) return "";
  try {
    return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, {
      weekday: "short", month: "short", day: "numeric",
    });
  } catch {
    return iso;
  }
}

// Deterministic cabin-category match. Returns the matched widgety cabin
// category, or null. A match requires EITHER a canonical identifier carried
// on the booking that equals a widgety cabin `id`, OR the authoritative booked
// category name equalling a widgety cabin `name` after normalization (exact,
// not fuzzy). We NEVER infer a match from the cabin number or partial
// name similarity — "Ocean View" must never match "Ocean View Balcony".
export function matchCabinCategory(cabinCategoryId, cabins, cabinCategory) {
  if (!Array.isArray(cabins)) return null;
  if (cabinCategoryId) {
    const byId = cabins.find((c) => c && c.id === cabinCategoryId);
    if (byId) return byId;
  }
  const target = normalizeName(cabinCategory);
  if (target) {
    return cabins.find((c) => c && normalizeName(c.name) === target) || null;
  }
  return null;
}

// Normalize a venue/category label for deterministic equality: lowercase,
// drop trademark/registered/service marks and any non-alphanumeric noise.
//   "Chops Grille℠" → "chopsgrille" · "Hooked Seafood℠" → "hookedseafood"
export function normalizeName(value) {
  if (!value) return "";
  return String(value)
    .toLowerCase()
    .replace(/[®™℠]/g, "")
    .replace(/&amp;/g, "and")
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "");
}

// Explicit, reviewed aliases for booked venue names that differ from the
// provider's canonical name. Keys/values are NORMALIZED. Only add an entry
// when the mapping is unambiguous for this cruise line.
const DINING_ALIASES = {
  izumihibachiandsushi: "izumi",
  izumihibachisushi: "izumi",
  windjammermarketplace: "windjammercaf",   // Widgety deck listing vs "Windjammer Café"
};

// Deterministic dining-venue match: exact normalized name, then a reviewed
// alias. Returns the widgety dining entry or null (→ honest fallback).
export function matchDiningVenue(venueName, diningList) {
  if (!venueName || !Array.isArray(diningList)) return null;
  const norm = normalizeName(venueName);
  if (!norm) return null;
  const exact = diningList.find((d) => d && normalizeName(d.name) === norm);
  if (exact) return exact;
  const alias = DINING_ALIASES[norm];
  if (alias) return diningList.find((d) => d && normalizeName(d.name) === alias) || null;
  return null;
}

// Reviewed aliases for booked entertainment whose authoritative title/venue
// differs from the provider's canonical name. Keys/values are NORMALIZED.
// SHOW aliases resolve to a Widgety record that IS the show → show-specific
// media. VENUE aliases resolve to the performance venue → venue imagery. Only
// add an entry when the mapping is unambiguous and reviewed for this line.
const ENT_SHOW_ALIASES = {
  hairsraythebroadwaymusical: "hairspray", // corrects a known booking-title typo ("Hairsray")
  hairspraythebroadwaymusical: "hairspray", // correctly-spelled title carries a ": The Broadway Musical" suffix
  flightdaretodream: "flight",             // "Flight...Dare to Dream" → the show "Flight"
};
const ENT_VENUE_ALIASES = {
  aquatheater: "aquatheatreshows",         // "Aqua Theater" → Widgety "AquaTheatre Shows"
  aquatheatre: "aquatheatreshows",
};

// Display-only corrections for a known, reviewed typo in an authoritative
// booking title. The SOURCE record is never mutated and matching still uses
// the raw title (via the alias above) — this only fixes what the guest sees.
const ENT_TITLE_DISPLAY = {
  hairsraythebroadwaymusical: "Hairspray: The Broadway Musical",
};
export function displayShowTitle(title) {
  return ENT_TITLE_DISPLAY[normalizeName(title)] || title || "";
}

// Deterministic entertainment match WITH media provenance. Show identity wins:
//   1. exact normalized title === a Widgety record name  → { mediaKind: "show" }
//   2. reviewed show alias → a Widgety record name        → { mediaKind: "show" }
// Otherwise fall back to the performance venue (media is clearly venue-level):
//   3. exact normalized venue === a Widgety record name   → { mediaKind: "venue" }
//   4. reviewed venue alias → a Widgety record name        → { mediaKind: "venue" }
// No fuzzy/partial matching — an unmatched show renders a branded fallback.
// Returns { record, mediaKind: "show" | "venue" } or null.
export function matchEntertainment(title, venue, venues) {
  if (!Array.isArray(venues)) return null;
  const byName = (name) => (name ? venues.find((x) => x && normalizeName(x.name) === name) || null : null);

  const t = normalizeName(title);
  if (t) {
    const exact = byName(t);
    if (exact) return { record: exact, mediaKind: "show" };
    const aliased = byName(ENT_SHOW_ALIASES[t]);
    if (aliased) return { record: aliased, mediaKind: "show" };
  }

  const v = normalizeName(venue);
  if (v) {
    const exact = byName(v);
    if (exact) return { record: exact, mediaKind: "venue" };
    const aliased = byName(ENT_VENUE_ALIASES[v]);
    if (aliased) return { record: aliased, mediaKind: "venue" };
  }
  return null;
}

// "18:30" (24-hour, as stored) → "6:30 PM". Passes through anything that is
// not a clean HH:MM so we never fabricate or corrupt a time.
export function formatReservationTime(t) {
  if (!t || typeof t !== "string") return "";
  const m = t.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return t;
  let hh = parseInt(m[1], 10);
  const mm = m[2];
  if (hh < 0 || hh > 23) return t;
  const period = hh >= 12 ? "PM" : "AM";
  const h12 = hh % 12 === 0 ? 12 : hh % 12;
  return `${h12}:${mm} ${period}`;
}

// Only render http(s) media URLs (defence-in-depth alongside backend
// filtering). Idempotent: standards-based `new URL` parsing preserves valid
// existing escapes (e.g. `%20` stays `%20`, never becomes `%2520`) and
// percent-encodes literal spaces in Widgety CDN filenames. Returns null for
// anything unsafe (javascript:, data:, relative, malformed).
export function safeMediaUrl(url) {
  if (typeof url !== "string") return null;
  const raw = url.trim();
  if (!/^https?:\/\//i.test(raw)) return null;
  try {
    const parsed = new URL(raw);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null;
    return parsed.href;
  } catch {
    return null;
  }
}

// First renderable image for a record's canonical media array.
export function firstImage(media) {
  if (!Array.isArray(media)) return null;
  for (const m of media) {
    if (m && (m.type === "image" || !m.type)) {
      const url = safeMediaUrl(m.url);
      if (url) return { ...m, url };
    }
  }
  return null;
}

export function safeMediaList(media) {
  if (!Array.isArray(media)) return [];
  return media
    .map((m) => (m ? { ...m, url: safeMediaUrl(m.url) } : m))
    .filter((m) => m && m.url);
}
